import React, { useEffect, useRef, useState } from 'react';
import {
  BAR_PAD_X,
  DOC_ICON_SIZE,
  LINE_STEP,
  RAIL_WIDTH,
} from '../utils/constants';
import { getMinimapImage, subscribeMinimapImages } from '../utils/minimapImageCache';

/**
 * Canvas renderer — the only way to get TRUE 1-device-pixel lines.
 * DOM hairlines smear to 2px whenever display scaling / page zoom
 * puts the rail at a fractional device offset; a canvas sized in
 * physical pixels owns its grid outright.
 */
export const MinimapCanvas = ({ items, squash, sceneW, trackH }) => {
  const canvasRef = useRef(null);
  // Bump when a cached thumbnail finishes loading so we re-paint
  const [imgTick, setImgTick] = useState(0);
  useEffect(() => subscribeMinimapImages(() => setImgTick((n) => n + 1)), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const devW = Math.max(1, Math.round(RAIL_WIDTH * dpr));
    const devH = Math.max(1, Math.round(trackH * dpr));
    if (canvas.width !== devW) canvas.width = devW;
    if (canvas.height !== devH) canvas.height = devH;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, devW, devH);

    // All drawing happens in integer DEVICE pixels
    const X = (cssX) => Math.round((BAR_PAD_X + cssX) * dpr);
    const Y = (miniY) => Math.round(miniY * squash * dpr);
    const HAIR = 1; // one physical pixel, guaranteed
    const STROKE = Math.max(2, Math.round(1.5 * dpr)); // titles/headings
    const RADIUS = Math.max(1.5, 2 * dpr); // tiny curve on boxes, like the page

    const fillRound = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, Math.min(r, h / 2, w / 2));
      ctx.fill();
    };
    const strokeRound = (x, y, w, h, r) => {
      ctx.beginPath();
      ctx.roundRect(x + 0.5, y + 0.5, w - 1, h - 1, Math.min(r, h / 2, w / 2));
      ctx.stroke();
    };

    // Callout boxes paint under their content
    const ordered = [...items].sort((a, b) => {
      const aBox = a.variant === 'callout' || a.variant === 'choice' ? 0 : 1;
      const bBox = b.variant === 'callout' || b.variant === 'choice' ? 0 : 1;
      if (a.miniTop === b.miniTop) return aBox - bBox;
      return a.miniTop - b.miniTop;
    });

    ordered.forEach((item) => {
      const x = X(item.leftFrac * sceneW);
      const w = Math.max(3, Math.round(item.widthFrac * sceneW * dpr) - 1);
      const y = Y(item.miniTop);
      const h = Math.max(HAIR, Y(item.miniTop + item.miniH) - y);

      if (item.variant === 'text' || item.variant === 'checkbox') {
        const step = LINE_STEP * squash * dpr;
        const isCheckbox = item.variant === 'checkbox';
        // Checkbox: a tiny box before the text — cyan when checked
        let textX = x;
        if (isCheckbox) {
          const boxSize = Math.max(2, STROKE);
          ctx.fillStyle = item.checked
            ? 'rgba(0, 217, 255, 0.9)'
            : item.indeterminate
              ? 'rgba(255, 255, 255, 0.5)'
              : 'rgba(255, 255, 255, 0.35)';
          ctx.fillRect(x, y, boxSize, boxSize);
          textX = x + boxSize + Math.max(1, Math.round(dpr));
        }
        // Checked label → --doc-text-dimmed; unchecked / body → muted hairlines
        const ink = isCheckbox
          ? item.checked
            ? 'rgba(255, 255, 255, 0.32)'
            : 'rgba(255, 255, 255, 0.3)'
          : 'rgba(255, 255, 255, 0.25)';
        item.lineFracs.forEach((frac, i) => {
          const lineY = y + Math.round(i * step);
          if (lineY > y + h) return;
          const lineX = i === 0 ? textX : x;
          const lineW = Math.max(2, Math.round(w * frac) - (lineX - x));
          ctx.fillStyle = ink;
          ctx.fillRect(lineX, lineY, lineW, HAIR);
          // Coloured variable pills — paint over the hairline at their real x
          const vars = item.lineVars?.[i];
          if (vars?.length) {
            vars.forEach((seg) => {
              if (!seg?.width) return;
              const segX = x + Math.round(w * (seg.start || 0));
              const segW = Math.max(1, Math.round(w * seg.width));
              ctx.fillStyle = seg.color || ink;
              ctx.fillRect(segX, lineY, segW, HAIR);
            });
          }
        });
        return;
      }
      if (item.variant === 'doc-icon') {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
        const size = Math.round(DOC_ICON_SIZE * dpr);
        fillRound(x, y, size, Math.max(2, Math.round(size * squash)), RADIUS);
        return;
      }
      if (item.variant === 'doc-title') {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
        ctx.fillRect(x, y, Math.max(10, Math.round(w * 0.6)), STROKE);
        return;
      }
      if (item.variant === 'heading') {
        const frac = item.lineFracs?.[0] ?? 0.45;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
        ctx.fillRect(x, y, Math.max(6, Math.round(w * frac)), STROKE);
        // Variable pills in the heading — coloured segments at their real x
        const vars = item.lineVars?.[0];
        if (vars?.length) {
          vars.forEach((seg) => {
            if (!seg?.width) return;
            ctx.fillStyle = seg.color || 'rgba(255, 255, 255, 0.55)';
            ctx.fillRect(
              x + Math.round(w * (seg.start || 0)),
              y,
              Math.max(1, Math.round(w * seg.width)),
              STROKE
            );
          });
        }
        return;
      }
      if (item.variant === 'divider') {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.14)';
        ctx.fillRect(x, y, w, HAIR);
        return;
      }
      if (item.variant === 'blank') return; // spacing only — no visible bar
      if (item.variant === 'choice') {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
        fillRound(x, y, w, h, RADIUS);
        ctx.fillStyle = 'rgba(0, 217, 255, 0.28)';
        ctx.fillRect(x + Math.round(dpr), y + Math.round(dpr), Math.max(8, Math.round(w * 0.28)), STROKE + 1);
        return;
      }
      if (item.variant === 'callout') {
        if (!item.surface) return; // quote variant — children draw themselves
        if (item.surface.borderMode) {
          ctx.strokeStyle = item.surface.color;
          ctx.lineWidth = HAIR;
          strokeRound(x, y, w, h, RADIUS);
        } else {
          ctx.fillStyle = item.surface.color;
          fillRound(x, y, w, h, RADIUS);
        }
        return;
      }
      if (item.variant === 'toggle') {
        // Chevron: right-pointing when closed, down-pointing when open
        const s = Math.max(2, STROKE + 1);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
        ctx.beginPath();
        if (item.open) {
          ctx.moveTo(x, y);
          ctx.lineTo(x + s, y);
          ctx.lineTo(x + s / 2, y + s);
        } else {
          ctx.moveTo(x, y);
          ctx.lineTo(x + s, y + s / 2);
          ctx.lineTo(x, y + s);
        }
        ctx.closePath();
        ctx.fill();
        // Title stroke next to the chevron, slightly brighter than body text
        ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
        const titleX = x + s + Math.max(1, Math.round(dpr));
        ctx.fillRect(titleX, y, Math.max(6, Math.round(w * 0.4)), STROKE);
        // Variable pills in the toggle title
        const vars = item.lineVars?.[0];
        if (vars?.length) {
          vars.forEach((seg) => {
            if (!seg?.width) return;
            ctx.fillStyle = seg.color || 'rgba(255, 255, 255, 0.45)';
            ctx.fillRect(
              x + Math.round(w * (seg.start || 0)),
              y,
              Math.max(1, Math.round(w * seg.width)),
              STROKE
            );
          });
        }
        return;
      }
      if (item.variant === 'code') {
        // Glyph runs per source line — spaces become gaps (ASCII trees, columns).
        // layout `lines` may be taller (wrap + header) than source runs; skip
        // header slots so content aligns with the doc↔mini scroll map.
        ctx.fillStyle = item.surface?.fill || 'rgb(26, 31, 39)';
        fillRound(x, y, w, h, RADIUS);
        ctx.fillStyle = item.surface?.line || 'rgba(255, 255, 255, 0.4)';
        const step = LINE_STEP * squash * dpr;
        const padY = Math.max(1, Math.round(2 * squash * dpr));
        const padX = Math.max(2, Math.round(2 * dpr));
        const inner = Math.max(1, w - padX * 2);
        const headerSkip = Math.max(0, item.headerLines || 0) * step;
        (item.lineFracs || []).forEach((lineSeg, i) => {
          const lineY = y + padY + headerSkip + Math.round(i * step);
          if (lineY > y + h - padY) return;
          // New: Array<{start,width}> per line. Legacy: number | {start,width}
          const runs = Array.isArray(lineSeg)
            ? lineSeg
            : typeof lineSeg === 'number'
              ? [{ start: 0, width: lineSeg }]
              : lineSeg
                ? [lineSeg]
                : [];
          ctx.fillStyle = item.surface?.line || 'rgba(255, 255, 255, 0.4)';
          runs.forEach((seg) => {
            const start = seg.start || 0;
            const width = seg.width || 0;
            if (width <= 0) return;
            ctx.fillRect(
              x + padX + Math.round(inner * start),
              lineY,
              Math.max(1, Math.round(inner * width)),
              HAIR
            );
          });
          // Coloured variable pills inside code — panel-relative segments
          const vars = item.lineVars?.[i];
          if (vars?.length) {
            vars.forEach((seg) => {
              if (!seg?.width) return;
              ctx.fillStyle = seg.color || item.surface?.line || 'rgba(255, 255, 255, 0.4)';
              ctx.fillRect(
                x + padX + Math.round(inner * (seg.start || 0)),
                lineY,
                Math.max(1, Math.round(inner * seg.width)),
                HAIR
              );
            });
          }
        });
        return;
      }
      if (item.variant === 'table') {
        // Same card language as the variables navigator: muted chrome, then
        // header + body stubs using the live column fractions.
        const pad = Math.max(1, Math.round(2 * squash * dpr));
        const step = LINE_STEP * squash * dpr;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
        fillRound(x, y, w, h, RADIUS);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
        ctx.lineWidth = HAIR;
        strokeRound(x, y, w, h, RADIUS);

        const innerX = x + pad;
        const innerW = Math.max(3, w - pad * 2);
        let lineY = y + pad;

        const colFracs = item.colFracs?.length
          ? item.colFracs
          : null;
        const headerCells = item.header?.cells;
        const bodyRows = item.rows || [];
        const colCount = Math.max(
          1,
          colFracs?.length || 0,
          headerCells?.length || 0,
          ...bodyRows.map((row) => row.cells?.length || 0)
        );
        const fracSum = (colFracs || []).reduce((a, b) => a + b, 0);
        const paints = colFracs && fracSum > 0
          ? colFracs.map((frac) => frac / fracSum)
          : Array.from({ length: colCount }, () => 1 / colCount);
        const gap = Math.max(1, Math.round(dpr));

        const paintRow = (cells, alpha) => {
          let cx = innerX;
          paints.forEach((frac, ci) => {
            const colW = Math.max(1, Math.round((innerW - gap * (paints.length - 1)) * frac));
            const cell = cells?.[ci];
            const fracW = typeof cell === 'number' ? cell : cell?.width ?? 0.14;
            const ink =
              typeof cell === 'object' && cell?.color
                ? cell.color
                : `rgba(255, 255, 255, ${cell?.upload ? alpha + 0.08 : alpha})`;
            ctx.fillStyle = ink;
            const stubH = cell?.upload ? Math.max(HAIR, Math.round(1.5 * dpr)) : HAIR;
            ctx.fillRect(cx, lineY, Math.max(1, Math.round(colW * fracW)), stubH);
            cx += colW + gap;
          });
        };

        paintRow(headerCells, 0.22);
        lineY += step;

        if (item.empty || bodyRows.length === 0) {
          ctx.fillStyle = 'rgba(161, 161, 170, 0.35)';
          ctx.fillRect(innerX, lineY, Math.max(4, Math.round(innerW * 0.4)), HAIR);
          lineY += step;
        } else {
          bodyRows.forEach((row) => {
            if (lineY > y + h - pad) return;
            paintRow(row.cells, 0.32);
            lineY += step;
          });
        }

        if (item.hasAdd && lineY <= y + h - pad) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
          ctx.fillRect(innerX, lineY, Math.max(3, Math.round(innerW * 0.22)), HAIR);
        }
        return;
      }
      if (item.variant === 'variables') {
        // Faithful mini of the in-doc variables block: soft chrome, "VARIABLES"
        // label, header stubs, then color-dot + name/value/desc/scope rows
        // (inherited rows stay dimmed like the real table).
        const pad = Math.max(1, Math.round(2 * squash * dpr));
        const step = LINE_STEP * squash * dpr;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
        fillRound(x, y, w, h, RADIUS);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
        ctx.lineWidth = HAIR;
        strokeRound(x, y, w, h, RADIUS);

        const innerX = x + pad;
        const innerW = Math.max(3, w - pad * 2);
        let lineY = y + pad;

        // Label — short muted uppercase stub
        ctx.fillStyle = 'rgba(113, 113, 122, 0.95)';
        ctx.fillRect(innerX, lineY, Math.max(4, Math.round(innerW * 0.28)), HAIR);
        lineY += step;

        const colFracs = item.colFracs || [0.05, 0.22, 0.1, 0.24, 0.2, 0.16];
        const colorW = Math.max(1, Math.round(innerW * (colFracs[0] || 0.05)));
        const textCols = colFracs.slice(1);
        const textSpan = textCols.reduce((a, b) => a + b, 0) || 1;
        const textStart = innerX + colorW + Math.max(1, Math.round(dpr));
        const textW = Math.max(2, innerW - colorW - Math.max(1, Math.round(dpr)));

        const paintTextCols = (cells, alpha) => {
          let cx = textStart;
          textCols.forEach((frac, ci) => {
            const colW = Math.max(1, Math.round(textW * ((frac || 0) / textSpan)));
            const fill = cells?.[ci] ?? 0.2;
            ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
            ctx.fillRect(cx, lineY, Math.max(1, Math.round(colW * fill)), HAIR);
            cx += colW;
          });
        };

        // Header row (no color dot)
        paintTextCols(item.header?.cells, 0.22);
        lineY += step;

        const bodyRows = item.rows || [];
        if (item.empty || bodyRows.length === 0) {
          ctx.fillStyle = 'rgba(161, 161, 170, 0.35)';
          ctx.fillRect(textStart, lineY, Math.max(4, Math.round(textW * 0.4)), HAIR);
          lineY += step;
        } else {
          bodyRows.forEach((row) => {
            if (lineY > y + h - pad) return;
            const alpha = row.readonly ? 0.16 : 0.32;
            // Color swatch
            const dot = Math.max(1, Math.min(colorW - 1, Math.round(1.5 * dpr)));
            ctx.fillStyle = row.color || `rgba(255, 255, 255, ${alpha})`;
            if (row.readonly) ctx.globalAlpha = 0.55;
            ctx.fillRect(innerX, lineY, dot, HAIR);
            ctx.globalAlpha = 1;
            paintTextCols(row.cells, alpha);
            lineY += step;
          });
        }

        if (item.hasAdd && lineY <= y + h - pad) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
          ctx.fillRect(innerX, lineY, Math.max(3, Math.round(innerW * 0.22)), HAIR);
        }
        return;
      }
      if (item.variant === 'media') {
        const thumb = getMinimapImage(item.src);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
        fillRound(x, y, w, h, RADIUS);
        if (thumb && thumb.naturalWidth > 0) {
          ctx.save();
          ctx.beginPath();
          ctx.roundRect(x, y, w, h, Math.min(RADIUS, h / 2, w / 2));
          ctx.clip();
          // Cover the mini slot so small images still read as a photo
          const scale = Math.max(w / thumb.naturalWidth, h / thumb.naturalHeight);
          const dw = thumb.naturalWidth * scale;
          const dh = thumb.naturalHeight * scale;
          ctx.drawImage(thumb, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
          ctx.restore();
        }
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.14)';
        ctx.lineWidth = HAIR;
        strokeRound(x, y, w, h, RADIUS);
        return;
      }
      ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
      fillRound(x, y, w, h, RADIUS);
    });
  }, [items, squash, sceneW, trackH, imgTick]);

  return (
    <canvas
      ref={canvasRef}
      className="doc-minimap__canvas"
      style={{ width: RAIL_WIDTH, height: trackH }}
    />
  );
};
